import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { HireService } from "./hire.service";
import { ProfileService } from "./profile.service";

/**
 * Navegacao dos avisos.
 *
 * Vive num servico — e nao dentro do NotificationComponent — porque o toque numa
 * notificacao push precisa levar exatamente ao mesmo lugar que o toque no aviso
 * correspondente na lista. Duas copias da regra divergiriam na primeira mudanca.
 */

/** Campos que a decisao de rota usa. Vindos do push chegam todos como string. */
export interface AlertLike {
  alert_type?: number | string;
  work_id?: number | string;
  titulo?: string;
  prof_id?: number | string;
  role?: string;
  [key: string]: unknown;
}

const ALERT_TYPE_CONTA = 1;
const ALERT_TYPE_MENSAGEM = 2;

@Injectable({ providedIn: "root" })
export class AlertNavigationService {
  private router = inject(Router);
  private profileService = inject(ProfileService);
  private hiresService = inject(HireService);

  /**
   * Destino unico de um alerta.
   *
   * Na lista de avisos o usuario escolhe entre os botoes disponiveis; no toque
   * de um push nao ha escolha, entao a ordem abaixo espelha a prioridade das
   * condicoes de notification.component.html. Sem match, cai na propria lista
   * de avisos, que sempre e um destino valido.
   */
  navigateForAlert(alert: AlertLike | null | undefined): void {
    if (!alert) {
      this.goToAlertList();
      return;
    }

    const alertType = this.toNumber(alert.alert_type);
    const profId = this.toNumber(alert.prof_id);
    // work_id é UUID (ex: 00357624-ae99-...), NÃO numérico — nada de toNumber aqui.
    const workId = alert.work_id ? String(alert.work_id) : "";
    const titulo = alert.titulo ?? "";

    if (titulo === "Endereço não encontrado") {
      this.goToRegisterAddress();
      return;
    }

    if (alertType === ALERT_TYPE_CONTA) {
      this.goToRegisterBankAccount();
      return;
    }

    if (workId && titulo !== "Conta") {
      this.goToHiringDetails({ ...alert, work_id: workId });
      return;
    }

    if (alertType === ALERT_TYPE_MENSAGEM && titulo !== "Proposta enviada" && profId !== null) {
      const profile = this.profileService.getProfile();
      this.goToConversation(profId, profile?.id);
      return;
    }

    this.goToAlertList();
  }

  goToAlertList() {
    this.router.navigateByUrl("/alert").catch(() => {});
  }

  goToRegisterBankAccount() {
    this.router.navigateByUrl("/mybills");
  }

  goToRegisterAddress() {
    this.router.navigateByUrl("/mybills/mybills-card-endereco");
  }

  goToConversation(recipientId: number, profileId: number) {
    this.router.navigate([
      "/chat",
      {
        idUser: recipientId,
        idProfile: profileId,
      },
    ]);
  }

  /**
   * Abre a contratação de um alerta buscando SÓ aquele work.
   *
   * Antes: com o UUID em mãos, o caminho do contratado pedia
   * `workService.findAll()` (my/works sem `?page` — 6.822 queries no dev para o
   * usuário com 273 works) só para achar o item com um `find()`; e o caminho do
   * contratante pedia my/hires inteiro e ainda disparava um getById POR HIRE da
   * lista, para montar `specialities` que nem eram usados aqui.
   *
   * Agora o próprio `getById` (GET api/offers/<work>/) já devolve o item no
   * formato que hiring-details espera — nomes/fotos das duas pontas, status,
   * evaluated_by_*, stars, favoritos — então é uma requisição, ponto.
   */
  goToHiringDetails(item) {
    const workId = item.work_id;

    this.hiresService.getById(workId).subscribe({
      next: (details) => {
        const tipo = this.tipoDoUsuario(details, item.role);
        // Shows têm página própria; a distinção é o flag `show`, mesmo critério
        // de myworks/myhires/hire.
        if (details?.show) {
          this.router.navigate(["show-work"], {
            replaceUrl: true,
            queryParams: {
              type: tipo,
              work: workId,
              ...(tipo === "work"
                ? {
                    client_name: (details as Record<string, unknown>)["client_name"],
                    client_picture: (details as Record<string, unknown>)["client_picture"],
                  }
                : {}),
            },
          });
          return;
        }
        this.navegarParaContratacao(tipo, workId, details);
      },
      // Sem o detalhe ainda vale abrir a tela: ela refaz o getById no
      // ionViewWillEnter e se recompõe. Antes um erro aqui caía no fluxo da
      // lista; agora não há lista para cair.
      error: (e) => {
        console.log(e);
        this.navegarParaContratacao(this.tipoDoUsuario(null, item.role), workId, null);
      },
    });
  }

  /**
   * De que lado do work está quem abriu o alerta.
   *
   * O `role` do alerta é fonte fraca: a lista de avisos ganha o campo da
   * AlertaList, mas o `data` do push nem sempre chega com ele — e sem `role` o
   * default caía em "hire", abrindo a tela do CONTRATANTE (com o botão PAGAR)
   * para o contratado. O detalhe do work traz `professional_id`/`client_id`,
   * então quem decide é ele; `role` fica só como fallback de quando o getById
   * falha.
   */
  private tipoDoUsuario(details: unknown, role?: string): "work" | "hire" {
    const d = (details ?? {}) as Record<string, unknown>;
    const meuId = this.idUsuarioLogado();
    if (meuId !== null) {
      if (Number(d["professional_id"] ?? d["professional"]) === meuId) return "work";
      if (Number(d["client_id"] ?? d["client"]) === meuId) return "hire";
    }
    return role === "contracted" ? "work" : "hire";
  }

  /** Lido do storage — injetar AuthService aqui fecharia ciclo com PushService. */
  private idUsuarioLogado(): number | null {
    try {
      const user = JSON.parse(localStorage.getItem("currentAVUser") ?? "null");
      const id = Number(user?.id);
      return Number.isFinite(id) ? id : null;
    } catch {
      return null;
    }
  }

  /**
   * `work` no queryParam é o item que hiring-details usa como estado inicial
   * (`chatReload`). Repassa o que veio do getById e garante as chaves de
   * identificação que cada lado procura (`id` no work, `work` no hire).
   */
  private navegarParaContratacao(tipo: string, workId: string, details: unknown) {
    const base = (details as Record<string, unknown>) ?? {};
    const work = { ...base, id: workId, work: workId, work_id: workId };
    this.router.navigate(["hiring-details", workId], {
      replaceUrl: true,
      queryParams: { type: tipo, work: JSON.stringify(work) },
    });
  }

  /**
   * Do push todo valor chega string; da lista chega number. Só para os campos
   * realmente numéricos (alert_type, prof_id) — work_id é UUID.
   */
  private toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
}
